import React, { useState } from 'react';
import styled from 'styled-components';
// import { FaWhatsapp, FaEnvelope, FaUser, FaComment } from 'react-icons/fa'; // WhatsApp commented out - may use later
import { FaEnvelope, FaUser, FaComment } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import Icon from '../Icon/Icon';
import AnimatedCard from '../AnimatedCard/AnimatedCard';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  PUBLIC_KEY: '4i8G7EykATzzz6Tzb',
  SERVICE_ID: 'service_4h3nirm',
  TEMPLATE_ID: 'template_od1g6wd', // Contact form template with auto-reply
};

const ContactSection = styled.main`
  min-height: 100vh;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.md}`};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, ${({ theme }) => `${theme.colors.primary}11`} 100%);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.sm}`};
    min-height: calc(100vh - 70px);
  }
`;

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.desktop};
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const Title = styled.h1`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: clamp(2rem, 5vw, 3rem);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;


const InfoTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: clamp(1.2rem, 3vw, 1.5rem);
`;

const InfoDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.6;
`;

// WhatsApp button commented out - may use later
/*
const WhatsAppButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background-color: #25D366;
  color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    background-color: #128C7E;
    box-shadow: ${({ theme }) => theme.shadows.large};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  }
`;
*/


const FormTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: 0.9rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.primary};
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xl}`};
  border: 2px solid ${({ theme }) => `${theme.colors.primary}33`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }

  &::placeholder {
    color: ${({ theme }) => `${theme.colors.text}66`};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xl}`};
  border: 2px solid ${({ theme }) => `${theme.colors.primary}33`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }

  &::placeholder {
    color: ${({ theme }) => `${theme.colors.text}66`};
  }
`;

const SubmitButton = styled(motion.button)`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background-color: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    background-color: ${({ theme }) => `${theme.colors.accent}dd`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: ${({ theme }) => `${theme.colors.accent}66`};
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
  font-weight: 500;
  background-color: ${({ type, theme }) =>
    type === 'success'
      ? `${theme.colors.accent}22`
      : '#f8d7da'};
  color: ${({ type, theme }) =>
    type === 'success'
      ? theme.colors.primary
      : '#721c24'};
  border: 1px solid ${({ type, theme }) =>
    type === 'success'
      ? `${theme.colors.accent}44`
      : '#f5c6cb'};
`;

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Initialize EmailJS
  React.useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  // WhatsApp link commented out - may use later
  // const whatsappMessage = encodeURIComponent("Hello Radyofone! I'm reaching out from the website, your app is amazing!");
  // const whatsappLink = `https://wa.me/96103487488?text=${whatsappMessage}`;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      // Send email to support@radyofone.com
      // Template has auto-reply enabled, so user will automatically receive confirmation
      const mainResult = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log('Contact form email sent successfully:', mainResult);

      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully to support@radyofone.com. You should receive a confirmation email shortly!'
      });

      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Email sending failed:', error);
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or contact us via WhatsApp.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection>
      <Container>
        <Title>Get in Touch</Title>

        <ContentGrid>
          <ContactInfo>
            {/* WhatsApp card commented out - may use later
            <AnimatedCard delay={0}>
              <InfoTitle>Need Quick Help?</InfoTitle>
              <InfoDescription>
                For immediate assistance, chat with us on WhatsApp. Our support team is available 24/7 to help you with any questions about Radyofone.
              </InfoDescription>
              <WhatsAppButton
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon icon={FaWhatsapp} size={20} />
                Chat on WhatsApp
              </WhatsAppButton>
            </AnimatedCard>
            */}

            <AnimatedCard delay={0.2}>
              <InfoTitle>Send us an Email</InfoTitle>
              <InfoDescription>
                Have a detailed question or feedback? Use our contact form to send us an email directly to support@radyofone.com. We typically respond within 2-4 hours during business days.
              </InfoDescription>
            </AnimatedCard>
          </ContactInfo>

          <AnimatedCard delay={0.4}>
            <FormTitle>Contact Form</FormTitle>
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <InputLabel htmlFor="name">Full Name</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <Icon icon={FaUser} size={16} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="email">Email Address</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <Icon icon={FaEnvelope} size={16} />
                  </InputIcon>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    required
                  />
                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="subject">Subject</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <Icon icon={FaComment} size={16} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What's this about?"
                    required
                  />
                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="message">Message</InputLabel>
                <InputWrapper>
                  <InputIcon style={{ top: '12px' }}>
                    <Icon icon={FaComment} size={16} />
                  </InputIcon>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your question or feedback..."
                    required
                  />
                </InputWrapper>
              </InputGroup>

              {status && (
                <StatusMessage type={status.type}>
                  {status.message}
                </StatusMessage>
              )}

              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </Form>
          </AnimatedCard>
        </ContentGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;